import React, { useState, useEffect } from 'react';
import './SendNotification.css';

const SendNotification = ({ facultyId, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    receiver_type: 'course',
    receiver_id: '',
  });

  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const normalizeStudents = (rawStudents = []) =>
    (rawStudents || []).map((student) => ({
      std_id: student.std_id || student.student_id || '',
      std_name: student.std_name || student.student_name || 'Unknown',
      roll_no: student.roll_no || student.email || student.phone || '',
    }));

  // Fetch courses for faculty
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await fetch(`/api/faculty/${facultyId}/courses`);
        const data = await response.json();
        setCourses(data.courses || []);
      } catch (err) {
        console.error('Error fetching courses:', err);
      }
    };

    if (facultyId) {
      fetchCourses();
    }
  }, [facultyId]);

  // Fetch students when course is selected
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        if (formData.receiver_type === 'course' && formData.receiver_id) {
          console.log(`📚 Fetching students for course: ${formData.receiver_id}`);
          const response = await fetch(`/api/courses/${formData.receiver_id}/students`);
          const data = await response.json();
          
          if (data.success) {
            console.log(`✅ Found ${data.count} students`);
            setStudents(normalizeStudents(data.students));
          } else {
            console.error('❌ Failed to fetch students:', data.error);
            setStudents([]);
          }
        } else if (formData.receiver_type === 'single') {
          // Fetch all students for single student selection
          console.log('👥 Fetching all students for single selection');
          const response = await fetch('/api/students');
          const data = await response.json();
          
          if (data.success) {
            console.log(`✅ Found ${data.count} students`);
            setStudents(normalizeStudents(data.students));
          } else {
            console.error('❌ Failed to fetch students:', data.error);
            setStudents([]);
          }
        }
      } catch (err) {
        console.error('❌ Error fetching students:', err);
        setStudents([]);
      }
    };

    fetchStudents();
  }, [formData.receiver_type, formData.receiver_id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Reset receiver_id when changing type
      ...(name === 'receiver_type' && { receiver_id: '' })
    }));
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validation
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!formData.message.trim()) {
      setError('Message is required');
      return;
    }
    if (formData.receiver_type !== 'global' && !formData.receiver_id) {
      setError('Please select a recipient');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/notifications/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sender_id: facultyId,
          title: formData.title,
          message: formData.message,
          receiver_type: formData.receiver_type,
          receiver_id: formData.receiver_type === 'global' ? null : formData.receiver_id,
          sender_role: 'faculty',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send notification');
      }

      setSuccess(`Notification sent to ${data.recipients_count} students`);
      
      // Reset form
      setFormData({
        title: '',
        message: '',
        receiver_type: 'course',
        receiver_id: '',
      });

      // Close after success
      setTimeout(() => {
        onClose?.();
      }, 2000);

    } catch (err) {
      console.error('Error sending notification:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="send-notification-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h2>📢 Send Notification</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSendNotification} className="notification-form">
          {/* Title */}
          <div className="form-group">
            <label htmlFor="title">
              Notification Title <span className="required">*</span>
            </label>
            <input
              id="title"
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="e.g., Assignment Due Reminder"
              className="input-field"
            />
          </div>

          {/* Message */}
          <div className="form-group">
            <label htmlFor="message">
              Message <span className="required">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Write your notification message..."
              className="textarea-field"
              rows="4"
            />
          </div>

          {/* Receiver Type */}
          <div className="form-group">
            <label htmlFor="receiver_type">
              Send To <span className="required">*</span>
            </label>
            <select
              id="receiver_type"
              name="receiver_type"
              value={formData.receiver_type}
              onChange={handleInputChange}
              className="select-field"
            >
              <option value="course">📘 Students of a specific course</option>
              <option value="single">👤 Single student</option>
              <option value="global">🌍 All students (all my courses)</option>
            </select>
          </div>

          {/* Receiver ID Selection */}
          {formData.receiver_type === 'course' && (
            <div className="form-group">
              <label htmlFor="receiver_id">
                Select Course <span className="required">*</span>
              </label>
              <select
                id="receiver_id"
                name="receiver_id"
                value={formData.receiver_id}
                onChange={handleInputChange}
                className="select-field"
              >
                <option value="">-- Select a course --</option>
                {courses.map(course => (
                  <option key={course.course_id} value={course.course_id}>
                    {course.course_name} ({course.course_code})
                  </option>
                ))}
              </select>

              {/* Students Preview */}
              {formData.receiver_id && students.length > 0 && (
                <div className="students-preview">
                  <p className="preview-label">
                    📊 {students.length} student{students.length !== 1 ? 's' : ''} will receive this:
                  </p>
                  <div className="students-list">
                    {students.slice(0, 5).map(student => (
                      <div key={student.std_id} className="student-tag">
                        {student.std_name}
                      </div>
                    ))}
                    {students.length > 5 && (
                      <div className="student-tag more">
                        +{students.length - 5} more
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {formData.receiver_type === 'single' && (
            <div className="form-group">
              <label htmlFor="receiver_id">
                Select Student <span className="required">*</span>
              </label>
              <select
                id="receiver_id"
                name="receiver_id"
                value={formData.receiver_id}
                onChange={handleInputChange}
                className="select-field"
              >
                <option value="">-- Search and select a student --</option>
                {students.map(student => (
                  <option key={student.std_id} value={student.std_id}>
                    {student.std_name} ({student.roll_no})
                  </option>
                ))}
              </select>
            </div>
          )}

          {formData.receiver_type === 'global' && (
            <div className="info-box">
              ℹ️ This notification will be sent to all students enrolled in all your courses.
            </div>
          )}

          {/* Status Messages */}
          {success && <div className="success-message">✓ {success}</div>}
          {error && <div className="error-message">✕ {error}</div>}

          {/* Buttons */}
          <div className="button-group">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-send"
              disabled={loading}
            >
              {loading ? 'Sending...' : '📤 Send Notification'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendNotification;
